import { type ReactNode } from 'react';
import { CharlieButton } from '@ver.id/libs.ui.charlie';
import { type FlowAction } from '../../types';

type Props = {
  /** The main area. Centred; scrolls when the card reaches its maximum. */
  children: ReactNode;
  /** The filled button, on the right. At most one. */
  primary?: FlowAction;
  /** The outline button: the way back, on the left. */
  secondary?: FlowAction;
  /**
   * The 44px button row is reserved on every screen, even when empty, so the
   * main area keeps its position. Only the app list screens drop it.
   */
  buttonRow?: boolean;
  /** The help row: text links, or the "more apps" button. */
  help?: ReactNode;
  /**
   * Minimum height in pixels (the install screen's 358px). Long text can
   * still grow it, up to the shell's maximum.
   */
  minHeight?: number;
};

/**
 * Side by side while both labels fit half the row; otherwise each button
 * takes a full line, the primary on top (the row wraps in reverse). A label
 * wraps only when it is wider than the whole row.
 */
const BUTTON =
  'h-auto min-h-11 min-w-fit grow basis-[calc(50%-4px)] whitespace-normal rounded-(--button-radius,10px) py-2.5 font-semibold';

/**
 * EmbeddedCardLayout — Embedded Ronan's one layout: main area, button row, help row.
 *
 * Grows with its content between the heights its shell sets (270px to 440px
 * in the card), then the main area scrolls. One look in the card and the
 * drawer: the shell sets the sizes and the border (see shellSizes.ts).
 */
export default function EmbeddedCardLayout({
  children,
  primary,
  secondary,
  buttonRow = true,
  help,
  minHeight,
}: Props) {
  return (
    <div
      data-embedded-card=""
      className="box-border flex max-h-(--embedded-max-h) min-h-(--embedded-min-h) w-full flex-col rounded-(--embedded-radius) border-(length:--embedded-border) border-border bg-card px-4 pt-3.5 pb-2 text-sm text-foreground"
      style={minHeight ? { minHeight } : undefined}
    >
      <div
        data-embedded-main=""
        className="flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:thin]"
      >
        <div className="my-auto flex w-full flex-col">{children}</div>
      </div>

      {buttonRow && (
        <div className="mt-4 flex min-h-11 shrink-0 flex-wrap-reverse gap-2">
          {secondary && (
            <CharlieButton
              variant="outline"
              className={BUTTON}
              onClick={secondary.onClick}
            >
              {secondary.label}
            </CharlieButton>
          )}
          {primary && (
            <CharlieButton
              className={BUTTON}
              loading={primary.loading}
              onClick={primary.onClick}
            >
              {primary.label}
            </CharlieButton>
          )}
        </div>
      )}

      <div className="mt-2 flex min-h-(--embedded-help-row-h) shrink-0 items-center justify-center gap-1">
        {help}
      </div>
    </div>
  );
}
